// The pipeline ("Pipeline build") report editor: compiles the op-chain (or the
// inspected prefix) into a ViewConfig, runs the shared engine, and previews it
// through the shared ReportViewer — identical to a flat-built report. Owns its
// own reducer; the host page handles save/navigation.
import { useReducer } from 'react'
import { ReportViewer } from '../components/ReportViewer'
import { ThresholdControl } from '../components/ThresholdControl'
import { resolveView } from '../components/ViewSwitcher'
import { runQuery } from '../engine'
import { frameOf, FRAME_LIST } from '../frames'
import { opsToConfig } from '../pipeline-compile'
import { FrameId, Op } from '../pipeline-types'
import { OpConfig } from './OpConfig'
import { OpMenu } from './OpMenu'
import { Pipeline } from './Pipeline'
import { initialState, reducer } from './store'
import { Button } from './ui'

interface Props {
  /** Seed ops (editing an existing pipeline report); empty for a new one. */
  initialOps?: Op[]
  /** Frame the seed ops run against (defaults to the registry default). */
  initialFrame?: FrameId
  /** Called with the current chain + frame when the user saves. */
  onSave?: (ops: Op[], frame: FrameId) => void
}

export function PipelineBuilder({
  initialOps = [],
  initialFrame,
  onSave,
}: Props) {
  const [state, dispatch] = useReducer(
    reducer,
    initialState(initialOps, null, initialFrame)
  )
  const { frame, ops, view, threshold, cursor, insertAt, menuMode, pendingOp } =
    state

  // Switching frame discards the chain (columns differ); confirm if non-empty.
  const onFrame = (next: FrameId) => {
    if (next === frame) return
    if (
      ops.length &&
      !window.confirm('Switching frame clears the current pipeline. Continue?')
    )
      return
    dispatch({ t: 'SET_FRAME', frame: next })
  }

  // Inspected prefix (cursor) drives the preview; the full chain decides whether
  // more ops can be appended (a terminal metric is the end of the line).
  const eff = cursor == null ? ops.length : cursor
  const fullConfig = opsToConfig(ops, frame)
  const canAppend = !(fullConfig.kind === 'aggregate' && !fullConfig.groupBy)

  const previewConfig = {
    ...opsToConfig(ops, frame, eff),
    threshold: threshold ?? undefined,
  }
  const result = runQuery(previewConfig, frameOf(frame))
  const effView = resolveView(previewConfig, result, view)

  const typeLabel =
    previewConfig.kind === 'aggregate'
      ? previewConfig.groupBy
        ? previewConfig.groupBy.bucket
          ? 'Time-series frame'
          : 'Aggregate frame'
        : 'Metric'
      : 'Frame'

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      {/* top bar: frame picker + save */}
      <div className="flex items-center justify-between border-b border-line bg-surface-raised px-3.5 py-2.5">
        <span className="flex items-center gap-2 text-sm font-semibold text-fg">
          <span className="text-fg-secondary">Frame</span>
          <select
            value={frame}
            onChange={(e) => onFrame(e.target.value as FrameId)}
            className="rounded-md border border-line-form bg-surface px-2 py-1 text-xs font-normal text-fg"
          >
            {FRAME_LIST.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </span>
        {onSave && (
          <Button
            variant="primary"
            disabled={ops.length === 0}
            onClick={() => onSave(ops, frame)}
          >
            Save report…
          </Button>
        )}
      </div>

      {/* live preview — the shared report viewer */}
      <div className="min-w-0 px-4 py-3.5">
        <ReportViewer
          config={previewConfig}
          result={result}
          view={effView}
          onViewChange={(v) => dispatch({ t: 'SET_VIEW', view: v })}
        />
        {previewConfig.kind === 'aggregate' && (
          <div className="mt-3 max-w-md">
            <ThresholdControl
              config={previewConfig}
              onChange={(next) =>
                next.threshold
                  ? dispatch({ t: 'SET_THRESHOLD', threshold: next.threshold })
                  : dispatch({ t: 'CLEAR_THRESHOLD' })
              }
            />
          </div>
        )}
      </div>

      {/* pipeline / op-chain editor */}
      <div className="border-t border-line bg-surface-sunken px-3.5 py-2.5">
        <span className="text-xs text-fg-secondary">
          PIPELINE&nbsp;&nbsp;
          <span className="rounded-full border border-brand bg-basil-50 px-2 py-0.5 text-xxs text-brand">
            {typeLabel}
          </span>
        </span>
        <Pipeline
          ops={ops}
          frame={frame}
          eff={eff}
          insertAt={insertAt}
          canAppend={canAppend}
          dispatch={dispatch}
        />
        {menuMode === 'menu' && (
          <OpMenu
            ops={ops}
            insertAt={insertAt}
            frame={frame}
            dispatch={dispatch}
          />
        )}
        {menuMode === 'config' && pendingOp && (
          <OpConfig
            op={pendingOp}
            ops={ops}
            insertAt={insertAt}
            frame={frame}
            dispatch={dispatch}
          />
        )}
      </div>
    </div>
  )
}
